import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isLegalPage = location.pathname === '/mentions-legales' || 
                      location.pathname === '/politique-confidentialite' ||
                      location.pathname === '/conditions-utilisation';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    setIsMobileMenuOpen(false);
    
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const navHeight = 80;
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - navHeight;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        const navHeight = 80;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - navHeight;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const navLinks = [
    { label: 'Qui sommes-nous', id: 'qui-sommes-nous' },
    { label: 'Raison d\'être', id: 'raison-etre' },
    { label: 'Valeurs', id: 'valeurs' },
    { label: 'Gouvernance', id: 'gouvernance' },
    { label: 'Contact', id: 'contact' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || isLegalPage ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <button
              onClick={handleLogoClick}
              className="flex items-center gap-3 sm:gap-4 cursor-pointer"
            >
              <img
                src="https://static.readdy.ai/image/a16a140b1ca96878839a6d35b91aba7d/90e6c0507c04b23ac45764b988f5600d.png"
                alt="OTOROBOTS"
                className="h-10 sm:h-12 w-auto"
              />
              <div className="w-px h-8 sm:h-10 bg-gray-900"></div>
              <div className={`text-xs sm:text-sm font-light tracking-wide text-left ${isScrolled || isLegalPage ? 'text-gray-900' : 'text-white'}`}>
                OTOROBOTS<br />CANADA INC.
              </div>
            </button>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-8">
              <button
                onClick={() => scrollToSection('qui-sommes-nous')}
                className={`text-sm font-light transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled || isLegalPage ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                }`}
              >
                Qui sommes-nous
              </button>
              <button
                onClick={() => scrollToSection('raison-etre')}
                className={`text-sm font-light transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled || isLegalPage ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                }`}
              >
                Raison d'être
              </button>
              <button
                onClick={() => scrollToSection('valeurs')}
                className={`text-sm font-light transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled || isLegalPage ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                }`}
              >
                Valeurs
              </button>
              <button
                onClick={() => scrollToSection('gouvernance')}
                className={`text-sm font-light transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled || isLegalPage ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                }`}
              >
                Gouvernance
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="px-6 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Contact
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
                isScrolled || isLegalPage ? 'text-gray-900' : 'text-white'
              }`}
              aria-label="Menu"
            >
              {isMobileMenuOpen ? (
                <i className="ri-close-line text-2xl"></i>
              ) : (
                <i className="ri-menu-line text-2xl"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}></div>
          <div className="fixed top-20 left-0 right-0 bg-white shadow-xl">
            <div className="px-6 py-8 space-y-6">
              <button
                onClick={() => scrollToSection('qui-sommes-nous')}
                className="block w-full text-left text-base font-light text-gray-700 hover:text-gray-900 transition-colors cursor-pointer py-2"
              >
                Qui sommes-nous
              </button>
              <button
                onClick={() => scrollToSection('raison-etre')}
                className="block w-full text-left text-base font-light text-gray-700 hover:text-gray-900 transition-colors cursor-pointer py-2"
              >
                Raison d'être
              </button>
              <button
                onClick={() => scrollToSection('valeurs')}
                className="block w-full text-left text-base font-light text-gray-700 hover:text-gray-900 transition-colors cursor-pointer py-2"
              >
                Valeurs
              </button>
              <button
                onClick={() => scrollToSection('gouvernance')}
                className="block w-full text-left text-base font-light text-gray-700 hover:text-gray-900 transition-colors cursor-pointer py-2"
              >
                Gouvernance
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="block w-full px-6 py-3 bg-gray-900 text-white text-base font-medium rounded-full hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap text-center mt-4"
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
